import React, { useEffect, useState } from "react";
import { get_terms_data } from "../../../apis/Common";

// This year's terms and holidays in the school-year order, whatever their
// dates say: Summer, 1, Autumn, 2, Winter, 3, Spring, 4 ("S 1 A 2 W 3 S 4").
// A box is red when the student is in a class that term (student_class) and
// grey when not; the term running now (or the next one, between terms) has a
// bar under it. A slot with no term set up for the year is left faint. It
// replaces "Student type", which every sign-up set to Enroll whether or not a
// class followed.

const YEAR = new Date().getFullYear();
const DAY = 86400000;

const SLOTS = [
  { label: "S", name: "Summer Holiday", match: (t) => t.kind === "holiday" && /^summer/i.test(t.name) },
  { label: "1", name: "Term 1", match: (t) => t.kind !== "holiday" && String(t.name).trim() === "1" },
  { label: "A", name: "Autumn Holiday", match: (t) => t.kind === "holiday" && /^autumn/i.test(t.name) },
  { label: "2", name: "Term 2", match: (t) => t.kind !== "holiday" && String(t.name).trim() === "2" },
  { label: "W", name: "Winter Holiday", match: (t) => t.kind === "holiday" && /^winter/i.test(t.name) },
  { label: "3", name: "Term 3", match: (t) => t.kind !== "holiday" && String(t.name).trim() === "3" },
  { label: "S", name: "Spring Holiday", match: (t) => t.kind === "holiday" && /^spring/i.test(t.name) },
  { label: "4", name: "Term 4", match: (t) => t.kind !== "holiday" && String(t.name).trim() === "4" },
];

const termEnd = (t) =>
  t.end_date ? new Date(t.end_date) : new Date(new Date(t.start_date).getTime() + Number(t.max_week || 0) * 7 * DAY);

let yearSlots = null; // one request per page load, shared by every row
const loadYearSlots = () => {
  if (!yearSlots) {
    yearSlots = get_terms_data().then((res) => {
      const today = new Date();
      const terms = (Array.isArray(res?.data) ? res.data : [])
        .filter((t) => String(t.year) === String(YEAR) && t.start_date)
        .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
      const current =
        terms.find((t) => new Date(t.start_date) <= today && today < termEnd(t)) ||
        terms.find((t) => new Date(t.start_date) > today);
      const used = new Set();
      const slots = SLOTS.map((slot) => {
        const term = terms.find((t) => !used.has(t.id) && slot.match(t));
        if (term) used.add(term.id);
        return { ...slot, term, current: !!term && term.id === current?.id };
      });
      // any other holiday the year has goes on the end, by date
      terms
        .filter((t) => !used.has(t.id))
        .forEach((t) => slots.push({ label: String(t.name).trim().charAt(0).toUpperCase(), name: t.name, term: t, current: t.id === current?.id }));
      return slots;
    });
  }
  return yearSlots;
};

export const enrolledHeading = (
  <span title="Red: in a class that term. Bar underneath: the term now. Faint: not set up this year.">
    Enrolled {YEAR}
  </span>
);

const box = {
  display: "inline-block",
  minWidth: 20,
  padding: "1px 4px",
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: "16px",
  textAlign: "center",
};

const EnrolledTerms = ({ enrolled = [] }) => {
  const [slots, setSlots] = useState([]);
  useEffect(() => {
    let alive = true;
    loadYearSlots().then((list) => alive && setSlots(list));
    return () => {
      alive = false;
    };
  }, []);
  const ids = (enrolled || []).map(String);
  return (
    <span style={{ display: "inline-flex", gap: 3, alignItems: "center", flexWrap: "nowrap", paddingBottom: 3 }}>
      {slots.map((slot, i) => {
        if (!slot.term) {
          return (
            <span key={`x${i}`} title={`${YEAR} ${slot.name}: not set up`} style={{ ...box, color: "#d1d5db", border: "1px dashed #e5e7eb" }}>
              {slot.label}
            </span>
          );
        }
        const on = ids.includes(String(slot.term.id));
        return (
          <span
            key={slot.term.id}
            title={`${YEAR} ${slot.name}: ${on ? "in a class" : "not enrolled"}${slot.current ? " (now)" : ""}`}
            style={{
              ...box,
              background: on ? "#A6192E" : "#f3f4f6",
              color: on ? "#fff" : "#9ca3af",
              border: `1px solid ${on ? "#A6192E" : "#e5e7eb"}`,
              boxShadow: slot.current ? "0 3px 0 #111827" : "none",
            }}
          >
            {slot.label}
          </span>
        );
      })}
    </span>
  );
};

// Shown next to a student's name: signed up through the free assessment
// rather than for enrolment (students.student_type = 0).
export const FreeAssessmentTag = () => (
  <span
    title="Signed up through the free assessment"
    style={{ ...box, marginLeft: 6, background: "#eef2ff", color: "#4338ca", fontWeight: 500, whiteSpace: "nowrap" }}
  >
    Free assessment
  </span>
);

export default EnrolledTerms;
